import { DefaultRuleSheet, GroundFillsV0RuleSheet, ImpedanceRuleSheet, LayoutRules, NetTypeAutoLayoutPrioritySheet, PcbNodeTypes, } from "@buildwithflux/models";
import { systemDefaultRulesSuffix } from "../../rules";
import { PcbRuleSetType } from "../models";
export class PcbSystemDefaultLayoutRulesFactory {
    static cachedRuleSetMaps = new Map();
    static getSystemDefaultLayoutRules(documentUid) {
        const cachedRuleSetMaps = this.cachedRuleSetMaps.get(documentUid);
        if (cachedRuleSetMaps) {
            return cachedRuleSetMaps;
        }
        // Compile system default rules from rules config
        const defaultRuleSetsMap = {};
        this.addGenericRuleConfigBasedRules(documentUid, defaultRuleSetsMap);
        this.addDefaultRulesFromRuleSheet(documentUid, defaultRuleSetsMap, DefaultRuleSheet, PcbRuleSetType.systemDefaultGlobal);
        this.addDefaultRulesFromRuleSheet(documentUid, defaultRuleSetsMap, ImpedanceRuleSheet, PcbRuleSetType.systemDefaultGlobalSpecial);
        this.addDefaultRulesFromRuleSheet(documentUid, defaultRuleSetsMap, NetTypeAutoLayoutPrioritySheet, PcbRuleSetType.systemDefaultGlobalSpecial);
        this.addDefaultRulesFromRuleSheet(documentUid, defaultRuleSetsMap, GroundFillsV0RuleSheet, PcbRuleSetType.systemDefaultGlobalSpecial);
        // Cache the compiled rules
        this.cachedRuleSetMaps.set(documentUid, defaultRuleSetsMap);
        return defaultRuleSetsMap;
    }
    static addDefaultRulesFromRuleSheet(documentUid, ruleSets, ruleSheet, ruleSetType) {
        for (const [selector, ruleSet] of Object.entries(ruleSheet)) {
            for (const [name, value] of Object.entries(ruleSet)) {
                if (!LayoutRules[name].setByDefault) {
                    // Note: this block is here for feature parity with the previous version (before introducing
                    // default rulesheets). The purpose of !setByDefault is to avoid cluttering the rules UI
                    // with rules that aren't important until set. For system-wide defaults, that prevents
                    // every rule from showing on every node. For rulesheet defaults, the effect is less
                    // severe. In practice, it doesn't currently occur, but it could be that in future, eg
                    // we want to specify a default for the "linePath rule", apply it only to line nodes via
                    // a rulesheet -- but not have it show for all line nodes, because most of the time it's
                    // not important.
                    //
                    // That said: that begs a few questions. So if we ever do that, I think we'll want to
                    // think it through further. Eg if the default isn't set by default, then when is it
                    // actually used? Currently it won't be. So why even have a default? Maybe a better
                    // logic would be a "showByDefault" or "showWhenUnset" config parameter.
                    continue;
                }
                this.addSystemDefaultRule(documentUid, ruleSets, selector, name, value, ruleSetType);
            }
        }
    }
    static addGenericRuleConfigBasedRules(documentUid, ruleSets) {
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        Object.values(PcbNodeTypes).forEach((nodeType) => {
            const nodeTypeBaseRules = Object.values(LayoutRules).filter((ruleConfig) => {
                if (!ruleConfig.setByDefault) {
                    return false;
                }
                if (!ruleConfig.supportedNodeTypes && !ruleConfig.unsupportedNodeTypes) {
                    return true;
                }
                if (ruleConfig.supportedNodeTypes?.length === 0 && ruleConfig.unsupportedNodeTypes?.length === 0) {
                    return true;
                }
                if (ruleConfig.supportedNodeTypes &&
                    ruleConfig.supportedNodeTypes?.includes(nodeType) &&
                    (!ruleConfig.unsupportedNodeTypes?.includes(nodeType) || !ruleConfig.unsupportedNodeTypes)) {
                    return true;
                }
                if (!ruleConfig.supportedNodeTypes && !ruleConfig.unsupportedNodeTypes?.includes(nodeType)) {
                    return true;
                }
                return false;
            });
            // eslint-disable-next-line @buildwithflux/no-array-foreach
            nodeTypeBaseRules.forEach((ruleConfig) => {
                this.addSystemDefaultRule(documentUid, ruleSets, nodeType, ruleConfig.key, ruleConfig.default, PcbRuleSetType.systemDefaultGlobal);
            });
        });
    }
    static getDescriptionForSelector(selector) {
        const descriptions = {
            "net[current]": "Applied automatically to any net that has a value in its Current property.",
            "net[impedance]": "Applied automatically to any net that has an Impedance property.",
            "net[impedancePair]": "Applied automatically to differential pairs with impedance control.",
            'net[NetType="Power"]': "Applied automatically to power nets for auto-layout prioritization.",
            'net[NetType="High Speed"]': "Applied automatically to high-speed nets for auto-layout prioritization.",
            'net[NetType="Medium Speed"]': "Applied automatically to medium-speed nets for auto-layout prioritization.",
            'net[NetType="Low Speed"]': "Applied automatically to low-speed nets for auto-layout prioritization.",
            'net[NetType="Analog"]': "Applied automatically to analog nets for auto-layout prioritization.",
            'net[NetType="Ground"]': "Applied automatically to ground nets for auto-layout prioritization.",
            "net[name=GND]": "Applied automatically to nets named GND.",
        };
        return descriptions[selector] ?? "";
    }
    static addSystemDefaultRule(documentUid, ruleSets, selector, key, value, ruleSetType) {
        const ruleSetUid = `${selector}-${systemDefaultRulesSuffix}`;
        const ruleSet = ruleSets[ruleSetUid] ?? {
            uid: ruleSetUid,
            selector: selector,
            rules: {},
            type: ruleSetType,
            description: this.getDescriptionForSelector(selector),
        };
        ruleSet.rules[key] = {
            uid: `root-${key}-rule`,
            key: key,
            value: value,
            referenceOfDocumentUid: documentUid,
            order: 0,
        };
        ruleSets[ruleSetUid] = ruleSet;
    }
}
//# sourceMappingURL=PcbSystemDefaultLayoutRulesFactory.js.map